import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Search,
  RotateCw,
  Eye,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useOrdersByOwner, useUpdateOrderStatus, useShopsByOwner } from '../../../api';
import styles from './bookings.module.css';

const VendorBookings = () => {
  const user = useSelector((state) => state.auth.user);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const userId = user?._id;
  const { data: shops = [] } = useShopsByOwner(userId);
  const { data: orders = [], refetch } = useOrdersByOwner(userId);
  const updateStatusMutation = useUpdateOrderStatus();

  const shopById = useMemo(() => {
    const m = {};
    shops.forEach((s) => {
      if (s?._id) m[String(s._id)] = s.name || 'Shop';
    });
    return m;
  }, [shops]);

  // Statistics
  const stats = useMemo(() => {
    let pending = 0;
    let confirmed = 0;
    let completed = 0;
    let cancelled = 0;

    orders.forEach((o) => {
      const s = String(o.orderStatus || '').toLowerCase();
      if (s === 'pending') pending++;
      else if (s === 'confirmed') confirmed++;
      else if (s === 'completed') completed++;
      else if (s === 'cancelled') cancelled++;
    });

    return { total: orders.length, pending, confirmed, completed, cancelled };
  }, [orders]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const s = String(order.orderStatus || '').toLowerCase();
      const matchesStatus = statusFilter === 'all' || s === statusFilter;

      const q = searchTerm.toLowerCase();
      const customerName = order.userDetails?.name || '';
      const customerEmail = order.userDetails?.email || '';
      const orderNum = order.orderNumber || String(order._id);

      const matchesSearch =
        !q ||
        customerName.toLowerCase().includes(q) ||
        customerEmail.toLowerCase().includes(q) ||
        orderNum.toLowerCase().includes(q);

      return matchesStatus && matchesSearch;
    });
  }, [orders, statusFilter, searchTerm]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await updateStatusMutation.mutateAsync({ orderId, status: newStatus });
      toast.success(`Booking status updated to ${newStatus}`);
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder((prev) => ({ ...prev, orderStatus: newStatus }));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update booking status.');
    }
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'pending':
        return <span className={`${styles.statusBadge} ${styles.statusPending}`}>Pending</span>;
      case 'confirmed':
        return <span className={`${styles.statusBadge} ${styles.statusConfirmed}`}>Confirmed</span>;
      case 'completed':
        return <span className={`${styles.statusBadge} ${styles.statusCompleted}`}>Completed</span>;
      case 'cancelled':
        return <span className={`${styles.statusBadge} ${styles.statusCancelled}`}>Cancelled</span>;
      default:
        return <span className={`${styles.statusBadge} ${styles.statusPending}`}>{status}</span>;
    }
  };

  return (
    <div className={styles.container}>
      {/* Page Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Booking Management</h1>
          <p className={styles.subtitle}>
            Supervise scheduled customer appointments, slot confirmations, and service fulfillment.
          </p>
        </div>

        <button
          className={styles.btnInspect}
          style={{ padding: '0.4rem 0.75rem', gap: '6px' }}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RotateCw size={13} className={isRefreshing ? styles.rotating : ''} />
          <span>Refresh Bookings</span>
        </button>
      </div>

      {/* Stats Counter Strip */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statMeta}>
            <span className={styles.statCount}>{stats.total}</span>
            <span className={styles.statLabel}>Total Bookings</span>
          </div>
          <div className={`${styles.statIcon} ${styles.total}`}>
            <Calendar size={16} />
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statMeta}>
            <span className={styles.statCount}>{stats.pending}</span>
            <span className={styles.statLabel}>Pending Approval</span>
          </div>
          <div className={`${styles.statIcon} ${styles.pending}`}>
            <Clock size={16} />
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statMeta}>
            <span className={styles.statCount}>{stats.confirmed}</span>
            <span className={styles.statLabel}>Confirmed</span>
          </div>
          <div className={`${styles.statIcon} ${styles.confirmed}`}>
            <AlertCircle size={16} />
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statMeta}>
            <span className={styles.statCount}>{stats.completed}</span>
            <span className={styles.statLabel}>Completed</span>
          </div>
          <div className={`${styles.statIcon} ${styles.completed}`}>
            <CheckCircle2 size={16} />
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statMeta}>
            <span className={styles.statCount}>{stats.cancelled}</span>
            <span className={styles.statLabel}>Cancelled</span>
          </div>
          <div className={`${styles.statIcon} ${styles.cancelled}`}>
            <XCircle size={16} />
          </div>
        </div>
      </div>

      {/* Toolbar & Filter Tabs */}
      <div className={styles.toolbarCard}>
        <div className={styles.toolbarHeader}>
          <div className={styles.searchBox}>
            <Search size={14} style={{ color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search customer, order #…"
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className={styles.filterTabs}>
            {[
              { id: 'all', label: 'All', count: stats.total },
              { id: 'pending', label: 'Pending', count: stats.pending },
              { id: 'confirmed', label: 'Confirmed', count: stats.confirmed },
              { id: 'completed', label: 'Completed', count: stats.completed },
              { id: 'cancelled', label: 'Cancelled', count: stats.cancelled },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`${styles.filterBtn} ${statusFilter === tab.id ? styles.filterBtnActive : ''}`}
                onClick={() => setStatusFilter(tab.id)}
              >
                <span>{tab.label}</span>
                <span className={styles.filterCount}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Data Table */}
        <div className={styles.tableWrapper}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Shop Location</th>
                <th>Date &amp; Slot</th>
                <th>Services</th>
                <th>Total</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const shopName =
                    shopById[String(order.shopId?._id || order.shopId)] ||
                    order.shopName ||
                    'Salon Location';
                  const initial = (order.userDetails?.name || 'C').charAt(0).toUpperCase();

                  return (
                    <tr key={order._id}>
                      <td>
                        <div className={styles.customerCell}>
                          <div className={styles.customerAvatar}>{initial}</div>
                          <div className={styles.customerMeta}>
                            <span className={styles.customerName}>
                              {order.userDetails?.name || 'Guest User'}
                            </span>
                            <span className={styles.orderNum}>
                              #{order.orderNumber || String(order._id).slice(-6)}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 500 }}>
                          {shopName}
                        </span>
                      </td>

                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>
                            {new Date(order.createdAt).toLocaleDateString('en-IN', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            {new Date(order.createdAt).toLocaleTimeString('en-IN', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className={styles.serviceChips}>
                          {(order.orderItem || []).slice(0, 2).map((item, idx) => (
                            <span key={idx} className={styles.serviceChip}>
                              {item.service?.name || item.name || 'Service'}
                            </span>
                          ))}
                          {(order.orderItem || []).length > 2 && (
                            <span className={styles.serviceChip}>
                              +{order.orderItem.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>
                          ₹{(order.grandTotal || 0).toLocaleString()}
                        </span>
                      </td>

                      <td>{getStatusBadge(order.orderStatus)}</td>

                      <td style={{ textAlign: 'right' }}>
                        <button
                          className={styles.btnInspect}
                          onClick={() => setSelectedOrder(order)}
                        >
                          <Eye size={12} />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className={styles.emptyState}>
                      <Calendar size={32} style={{ color: '#94a3b8' }} />
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>
                        No appointments found
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Try adjusting your search query or filter criteria.
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className={styles.modalBackdrop} onClick={() => setSelectedOrder(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                Booking #{selectedOrder.orderNumber || String(selectedOrder._id).slice(-8)}
              </h3>
              <button className={styles.modalCloseBtn} onClick={() => setSelectedOrder(null)}>
                <X size={16} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Customer Information */}
              <div className={styles.detailCard}>
                <div className={styles.detailCardTitle}>Customer Information</div>
                <div className={styles.detailRow}>
                  <span style={{ color: '#64748b' }}>Name</span>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>
                    {selectedOrder.userDetails?.name || 'Guest User'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span style={{ color: '#64748b' }}>Email</span>
                  <span style={{ color: '#0f172a' }}>
                    {selectedOrder.userDetails?.email || '—'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span style={{ color: '#64748b' }}>Phone</span>
                  <span style={{ color: '#0f172a' }}>
                    {selectedOrder.userDetails?.phone || '—'}
                  </span>
                </div>
              </div>

              {/* Booked Services */}
              <div className={styles.detailCard}>
                <div className={styles.detailCardTitle}>Booked Services</div>
                {(selectedOrder.orderItem || []).map((item, idx) => (
                  <div key={idx} className={styles.detailRow}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#0f172a', display: 'block' }}>
                        {item.service?.name || item.name || 'Salon Service'}
                      </span>
                      {item.service?.duration && (
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                          Duration: {item.service.duration} mins
                        </span>
                      )}
                    </div>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>
                      ₹{Number(item.service?.price || item.price || 0).toLocaleString()}
                    </span>
                  </div>
                ))}

                <div
                  className={styles.detailRow}
                  style={{
                    borderTop: '2px solid #e2e8f0',
                    marginTop: '8px',
                    paddingTop: '8px',
                    fontSize: '0.9rem',
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>Grand Total</span>
                  <span style={{ fontWeight: 800, color: '#dc2626', fontSize: '1rem' }}>
                    ₹{(selectedOrder.grandTotal || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Status Update Quick Action */}
              <div className={styles.detailCard}>
                <div className={styles.detailCardTitle}>Current Status</div>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <div>{getStatusBadge(selectedOrder.orderStatus)}</div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Payment:{' '}
                    <strong style={{ color: '#059669' }}>
                      {selectedOrder.isPaid ? 'PAID' : 'PENDING'}
                    </strong>
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              {String(selectedOrder.orderStatus || '').toLowerCase() === 'pending' && (
                <button
                  className={`${styles.btnAction} ${styles.confirm}`}
                  onClick={() => handleUpdateStatus(selectedOrder._id, 'CONFIRMED')}
                  disabled={updateStatusMutation.isPending}
                >
                  Confirm Appointment
                </button>
              )}

              {String(selectedOrder.orderStatus || '').toLowerCase() === 'confirmed' && (
                <button
                  className={`${styles.btnAction} ${styles.complete}`}
                  onClick={() => handleUpdateStatus(selectedOrder._id, 'COMPLETED')}
                  disabled={updateStatusMutation.isPending}
                >
                  Mark as Completed
                </button>
              )}

              {String(selectedOrder.orderStatus || '').toLowerCase() !== 'cancelled' &&
                String(selectedOrder.orderStatus || '').toLowerCase() !== 'completed' && (
                  <button
                    className={`${styles.btnAction} ${styles.cancel}`}
                    onClick={() => handleUpdateStatus(selectedOrder._id, 'CANCELLED')}
                    disabled={updateStatusMutation.isPending}
                  >
                    Cancel Booking
                  </button>
                )}

              <button
                className={styles.btnInspect}
                style={{ padding: '0.4rem 0.85rem' }}
                onClick={() => setSelectedOrder(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorBookings;
